/*
 * Diamond Studio conversion fixes (2026-10-01)
 * 1. Complimentary diamond studs: adds the gift to the bag at US$3,000+ and removes it below that.
 * 2. Bag: shows ring add-ons (gold colour, engraving) inside their ring instead of as separate pieces;
 *    the bag count shows pieces, not add-on lines.
 * 3. MooseDesk chat button moves above sticky "Add to bag" bars so it never covers them.
 * 4. Search: "Best matches" row when a multi-word search has pieces that match every word.
 * Config comes from window.DS_CONV (snippets/ds-cro-config.liquid).
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) api.install(root);
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';

  var ENGRAVING_VARIANT = '49558118531310';

  function isChild(item) {
    return !!((item.parent_relationship && item.parent_relationship.parent_key) ||
      (item.properties && item.properties._gpo_parent_product_group));
  }
  function isGift(item, giftId) {
    return !!giftId && String(item.variant_id || item.id) === String(giftId);
  }
  function linePrice(item) {
    return Number(item.final_line_price != null ? item.final_line_price : item.line_price) || 0;
  }
  function pieceCount(cart) {
    return (cart.items || []).filter(function (i) { return !isChild(i); })
      .reduce(function (n, i) { return n + (Number(i.quantity) || 0); }, 0);
  }
  function qualifyingTotal(cart, giftId) {
    return (cart.items || []).filter(function (i) { return !isGift(i, giftId); })
      .reduce(function (s, i) { return s + linePrice(i); }, 0);
  }
  // What the bag should look like for the gift: returns null (nothing to do) or {add, updates}.
  function giftPlan(cart, giftId, threshold, blockedAt) {
    if (!giftId || !cart || !Array.isArray(cart.items)) return null;
    if (cart.currency && cart.currency !== 'USD') return null;
    var gifts = cart.items.filter(function (i) { return isGift(i, giftId); });
    var qty = gifts.reduce(function (n, i) { return n + (Number(i.quantity) || 0); }, 0);
    var total = qualifyingTotal(cart, giftId);
    var want = total >= threshold && blockedAt !== total;
    var updates = {};
    if (!want) {
      if (!qty) return null;
      gifts.forEach(function (g) { updates[g.key] = 0; });
      return { add: false, updates: updates, total: total };
    }
    if (!qty) return { add: true, updates: null, total: total };
    if (gifts.length === 1 && qty === 1) {
      if (linePrice(gifts[0]) === 0) return null;
      // In the bag but the discount did not make it free: take it out rather than charge for it.
      updates[gifts[0].key] = 0;
      return { add: false, updates: updates, total: total, notFree: true };
    }
    gifts.forEach(function (g, index) { updates[g.key] = index === 0 ? 1 : 0; });
    return { add: false, updates: updates, total: total };
  }
  function parentKeyFor(item, cart) {
    if (item.parent_relationship && item.parent_relationship.parent_key) return item.parent_relationship.parent_key;
    var group = item.properties && item.properties._gpo_parent_product_group;
    if (!group) return null;
    var parent = (cart.items || []).find(function (i) { return i.properties && i.properties._gpo_product_group === group && !isChild(i); });
    return parent ? parent.key : null;
  }
  function addonLabel(item) {
    var title = String(item.product_title || item.title || '');
    var variant = item.variant_title && item.variant_title !== 'Default Title' ? String(item.variant_title) : '';
    if (String(item.variant_id) === ENGRAVING_VARIANT || /engrav/i.test(title)) {
      var text = item.properties && item.properties.Engraving ? String(item.properties.Engraving) : '';
      return text ? 'Engraving: \u201c' + text + '\u201d' : 'Engraving';
    }
    if (/gold/i.test(title) && variant) return variant + ' gold';
    return variant ? title + ': ' + variant : title;
  }
  function searchWords(query) {
    return String(query || '').toLowerCase().split(/\s+/).map(function (w) { return w.replace(/[^a-z0-9.-]/g, ''); })
      .filter(function (w) { return w.length > 1; });
  }
  function matchesAll(product, words) {
    var hay = [product.title, product.type, (product.tags || []).join(' ')].join(' ').toLowerCase();
    return words.every(function (w) { return hay.indexOf(w) !== -1; });
  }

  function install(win) {
    if (win.DSConvFixes) return;
    var doc = win.document;
    var cfg = win.DS_CONV || {};
    var giftId = cfg.giftVariantId ? String(cfg.giftVariantId) : '';
    var threshold = Number(cfg.giftThreshold) || 300000;
    var currency = cfg.currency || 'USD';
    var rootUrl = function () { return (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/'; };
    var money = function (cents) {
      try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100); }
      catch (e) { return '$' + (cents / 100).toFixed(2); }
    };
    var esc = function (v) { return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };
    var cssKey = function (v) { return win.CSS && win.CSS.escape ? win.CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&'); };
    var lastCart = null, busy = false, badgeTimer = 0, selfBadge = false;
    var store = {
      get: function (k) { try { return win.sessionStorage.getItem(k); } catch (e) { return null; } },
      set: function (k, v) { try { win.sessionStorage.setItem(k, v); } catch (e) {} }
    };
    // Bag total at which the gift could not be made free; kept for the visit so we never loop.
    var blockedAt = store.get('ds_gift_blocked') ? Number(store.get('ds_gift_blocked')) : null;
    function block(total) { blockedAt = total; store.set('ds_gift_blocked', String(total)); }
    function reloadCartPage() {
      var last = Number(store.get('ds_gift_reload') || 0);
      if (Date.now() - last < 15000) return false;
      store.set('ds_gift_reload', String(Date.now()));
      win.location.reload();
      return true;
    }

    function getCart() {
      return win.fetch(rootUrl() + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
        .then(function (cart) { lastCart = cart; return cart; });
    }
    function post(path, body) {
      return win.fetch(rootUrl() + path, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
        .then(function (r) { if (!r.ok) throw new Error(path); return r.json(); });
    }

    /* ---------- bag count ---------- */
    function syncBadges(cart) {
      var n = pieceCount(cart);
      selfBadge = true;
      doc.querySelectorAll('[data-cart-count], .ds-header-cart-count').forEach(function (node) {
        if (node.textContent !== String(n)) node.textContent = String(n);
        node.hidden = !n;
      });
      selfBadge = false;
    }
    function scheduleBadgeSync() {
      if (selfBadge) return;
      win.clearTimeout(badgeTimer);
      badgeTimer = win.setTimeout(function () { getCart().then(syncBadges).catch(function () {}); }, 250);
    }

    /* ---------- complimentary studs ---------- */
    function reconcileGift(cart) {
      var plan = giftPlan(cart, giftId, threshold, blockedAt);
      if (!plan || busy) return Promise.resolve(false);
      busy = true;
      var step = plan.add
        ? post('cart/add.js', { items: [{ id: Number(giftId), quantity: 1, properties: { _ds_gift: 'studs-3000' } }] })
        : post('cart/update.js', { updates: plan.updates });
      return step.then(function () { return getCart(); }).then(function (fresh) {
        if (plan.add) {
          var line = fresh.items.find(function (i) { return isGift(i, giftId); });
          if (line && linePrice(line) !== 0) {
            block(plan.total);
            var undo = {}; undo[line.key] = 0;
            return post('cart/update.js', { updates: undo }).then(function () { return getCart(); });
          }
        }
        if (plan.notFree) block(plan.total);
        return fresh;
      }).then(function (fresh) { syncBadges(fresh); return true; })
        .catch(function () { return false; })
        .then(function (changed) { busy = false; return changed; });
    }

    /* ---------- bag drawer ---------- */
    function decorateDrawer(cart) {
      var list = doc.querySelector('.ds-bag-items');
      if (!list || !cart) return;
      var totals = {};
      cart.items.forEach(function (item) {
        if (!isChild(item)) return;
        var pkey = parentKeyFor(item, cart);
        var row = list.querySelector('[data-gpo-drawer-item-key="' + cssKey(item.key) + '"]');
        var prow = pkey && list.querySelector('[data-gpo-drawer-item-key="' + cssKey(pkey) + '"]');
        if (!row || !prow || row.hasAttribute('data-ds-addon')) return;
        row.hidden = true;
        row.setAttribute('data-ds-addon', '');
        var details = prow.querySelector('.ds-bag-details') || prow;
        var ul = details.querySelector('.ds-addon-list');
        if (!ul) {
          ul = doc.createElement('ul');
          ul.className = 'ds-addon-list';
          var price = details.querySelector(':scope > strong');
          details.insertBefore(ul, price || null);
        }
        var li = doc.createElement('li');
        li.innerHTML = '<span>' + esc(addonLabel(item)) + '</span><span>+' + esc(money(linePrice(item))) + '</span>';
        ul.appendChild(li);
        totals[pkey] = (totals[pkey] || 0) + linePrice(item);
      });
      Object.keys(totals).forEach(function (pkey) {
        var prow = list.querySelector('[data-gpo-drawer-item-key="' + cssKey(pkey) + '"]');
        var parent = cart.items.find(function (i) { return i.key === pkey; });
        if (!prow || !parent || prow.querySelector('.ds-addon-total')) return;
        var p = doc.createElement('p');
        p.className = 'ds-addon-total';
        p.textContent = 'Total with options: ' + money(linePrice(parent) + totals[pkey]);
        var ul = prow.querySelector('.ds-addon-list');
        if (ul && ul.parentNode) ul.parentNode.insertBefore(p, ul.nextSibling);
      });
      cart.items.forEach(function (item) {
        if (!isGift(item, giftId)) return;
        var row = list.querySelector('[data-gpo-drawer-item-key="' + cssKey(item.key) + '"]');
        if (!row || row.classList.contains('ds-gift-row')) return;
        row.classList.add('ds-gift-row');
        var remove = row.querySelector('.ds-bag-remove');
        if (remove) remove.remove();
        var name = row.querySelector('.ds-bag-name');
        if (name) name.insertAdjacentHTML('afterend', '<p class="ds-gift-tag">Complimentary with orders of US$3,000+</p>');
      });
      // Hide "-$0.00" discount lines that Shopify attaches to the ring that unlocked the gift.
      list.querySelectorAll('.ds-bag-item:not(.ds-gift-row) .ds-bag-discount').forEach(function (line) {
        if (/[−-]\s*\$0(\.00)?\s*$/.test(line.textContent)) line.hidden = true;
      });
      // The gift progress panel only reads totals that name the currency, so add it for screen readers.
      var sub = doc.querySelector('.ds-bag-footer .cart-modal-totalprice');
      if (sub && !/USD/.test(sub.textContent) && cart.currency === 'USD') {
        sub.insertAdjacentHTML('beforeend', '<span class="ds-visually-hidden"> USD</span>');
      }
    }

    /* ---------- /cart page ---------- */
    function decorateCartPage(cart) {
      var body = doc.querySelector('.cart-table__body');
      if (!body || !cart) return;
      body.querySelectorAll('[data-ds-property="image-swatches-1"]').forEach(function (node) {
        node.textContent = node.textContent.replace(/^image-swatches-1:/, 'Gold Color:');
      });
      cart.items.forEach(function (item) {
        var row = body.querySelector('.item-cart[data-gpo-cart-item-key="' + cssKey(item.key) + '"]');
        if (!row) return;
        if (isGift(item, giftId)) {
          row.classList.add('ds-gift-row');
          return;
        }
        if (!isChild(item)) return;
        var pkey = parentKeyFor(item, cart);
        var prow = pkey && body.querySelector('.item-cart[data-gpo-cart-item-key="' + cssKey(pkey) + '"]');
        if (!prow) return;
        row.hidden = true;
        var info = prow.querySelector('.info') || prow;
        var ul = info.querySelector('.ds-addon-list');
        if (!ul) {
          ul = doc.createElement('ul');
          ul.className = 'ds-addon-list';
          info.insertBefore(ul, info.querySelector('.cart-product-info__price'));
        }
        var key = 'addon-' + item.key;
        if (ul.querySelector('[data-k="' + cssKey(key) + '"]')) return;
        var li = doc.createElement('li');
        li.setAttribute('data-k', key);
        li.innerHTML = '<span>' + esc(addonLabel(item)) + '</span><span>+' + esc(money(linePrice(item))) + '</span>';
        ul.appendChild(li);
      });
    }

    function onBagRendered() {
      getCart().then(function (cart) {
        return reconcileGift(cart).then(function (changed) {
          if (changed && win.DSCartDrawer) { win.DSCartDrawer.refresh(); return; }
          decorateDrawer(lastCart);
          syncBadges(lastCart);
        });
      }).catch(function () {});
    }

    var cartPageTimer = 0, cartPageBusy = false;
    function onCartPageChange() {
      if (cartPageBusy) return;
      win.clearTimeout(cartPageTimer);
      cartPageTimer = win.setTimeout(function () {
        cartPageBusy = true;
        getCart().then(function (cart) {
          return reconcileGift(cart).then(function (changed) {
            if (changed && reloadCartPage()) return;
            decorateCartPage(lastCart || cart);
            syncBadges(lastCart || cart);
          });
        }).catch(function () {}).then(function () { win.setTimeout(function () { cartPageBusy = false; }, 50); });
      }, 200);
    }

    /* ---------- chat button above sticky bars ---------- */
    var STICKY = '.a-sticky-buy, [class*="sticky"]';
    function bottomBarHeight() {
      var vh = win.innerHeight, vw = win.innerWidth, max = 0;
      doc.querySelectorAll(STICKY).forEach(function (el) {
        if (el.id === 'moosedesk-widget-host' || el.closest('#PBarNextFrameWrapper')) return;
        var cs = win.getComputedStyle(el);
        if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
        if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) < 0.1) return;
        var r = el.getBoundingClientRect();
        if (r.height < 20 || r.height > 220 || r.width < vw * 0.5) return;
        if (r.bottom < vh - 120 || r.top >= vh) return;
        max = Math.max(max, vh - r.top);
      });
      return Math.round(max);
    }
    var liftFrame = 0;
    function liftChat() {
      liftFrame = 0;
      var host = doc.getElementById('moosedesk-widget-host');
      if (!host || !host.shadowRoot) return;
      if (!host.shadowRoot.getElementById('ds-chat-lift')) {
        var style = doc.createElement('style');
        style.id = 'ds-chat-lift';
        style.textContent = ':host([data-ds-lift]) .mdw-float-btn{bottom:calc(var(--ds-lift, 0px) + 12px)!important;transition:bottom .2s ease}';
        host.shadowRoot.appendChild(style);
      }
      var h = bottomBarHeight();
      if (h > 0) {
        host.style.setProperty('--ds-lift', h + 'px');
        host.setAttribute('data-ds-lift', '');
      } else {
        host.removeAttribute('data-ds-lift');
      }
    }
    function queueLift() { if (!liftFrame) liftFrame = win.requestAnimationFrame(liftChat); }

    /* ---------- search best matches ---------- */
    function bestMatches() {
      if (cfg.template !== 'search') return;
      var params = new win.URLSearchParams(win.location.search);
      var q = params.get('q') || '';
      var words = searchWords(q);
      if (words.length < 2 || (params.get('page') && params.get('page') !== '1')) return;
      var sp = new win.URLSearchParams();
      sp.set('q', q);
      sp.set('resources[type]', 'product');
      sp.set('resources[limit]', '10');
      sp.set('resources[options][unavailable_products]', 'hide');
      win.fetch(rootUrl() + 'search/suggest.json?' + sp.toString(), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = ((data.resources || {}).results || {}).products || [];
          var hits = products.filter(function (p) { return matchesAll(p, words); }).slice(0, 4);
          if (!hits.length || doc.querySelector('.ds-best-matches')) return;
          var cards = hits.map(function (p) {
            var img = (p.featured_image && p.featured_image.url) || p.image || '';
            var src = img ? img + (img.indexOf('?') === -1 ? '?' : '&') + 'width=400' : '';
            return '<a class="ds-best-matches__card" href="' + esc(p.url) + '">' +
              (src ? '<img src="' + esc(src) + '" alt="' + esc(p.title) + '" width="400" height="400" loading="lazy">' : '') +
              '<span class="ds-best-matches__title">' + esc(p.title) + '</span>' +
              '<span class="ds-best-matches__price">' + esc(money(Math.round(Number(p.price) * 100))) + '</span></a>';
          }).join('');
          var section = doc.createElement('section');
          section.className = 'ds-best-matches';
          section.setAttribute('aria-label', 'Best matches');
          section.innerHTML = '<h2>Best matches for \u201c' + esc(q) + '\u201d</h2><div class="ds-best-matches__grid">' + cards + '</div>' +
            '<p class="ds-best-matches__hint">More related pieces are below. Use the Shape and Price filters to narrow them down.</p>';
          var first = doc.querySelector('main a[href*="/products/"]');
          var anchor = first && (first.closest('.products__row, .product-grid, [class*="results"], ul, ol') || first.parentElement);
          if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(section, anchor);
          else (doc.querySelector('main') || doc.body).prepend(section);
        }).catch(function () {});
    }

    /* ---------- wiring ---------- */
    // The bag drawer (ds-header-cart-drawer.js) fires ds:bag-rendered after every render.
    // The gift progress panel (DSStudGiftCart in ds-shared-header.js) is left as is.
    doc.addEventListener('ds:bag-rendered', onBagRendered);

    function start() {
      getCart().then(function (cart) {
        return reconcileGift(cart).then(function (changed) {
          var c = lastCart || cart;
          if (changed && /\/cart\/?$/.test(win.location.pathname) && reloadCartPage()) return;
          syncBadges(c);
          decorateCartPage(c);
        });
      }).catch(function () {});

      var badgeObserver = new win.MutationObserver(scheduleBadgeSync);
      doc.querySelectorAll('[data-cart-count], .ds-header-cart-count').forEach(function (node) {
        badgeObserver.observe(node, { childList: true, characterData: true, subtree: true });
      });

      var cartForm = doc.getElementById('cart-form');
      if (cartForm) new win.MutationObserver(onCartPageChange).observe(cartForm, { childList: true, subtree: true });

      win.addEventListener('scroll', queueLift, { passive: true });
      win.addEventListener('resize', queueLift);
      doc.addEventListener('click', function () { win.setTimeout(queueLift, 350); }, true);
      var ticks = 0;
      var timer = win.setInterval(function () { queueLift(); if (++ticks > 30) win.clearInterval(timer); }, 1000);
      new win.MutationObserver(queueLift).observe(doc.body, { childList: true });

      bestMatches();
    }

    win.DSConvFixes = { version: '2026-10-01', refresh: onBagRendered, liftChat: queueLift };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }

  return { install: install, isChild: isChild, isGift: isGift, pieceCount: pieceCount, qualifyingTotal: qualifyingTotal, giftPlan: giftPlan, parentKeyFor: parentKeyFor, addonLabel: addonLabel, searchWords: searchWords, matchesAll: matchesAll };
});
